import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { Check, EllipsisVertical } from "lucide-react";

export interface MenuItem {
  label: string;
  onSelect: () => void;
  /** Destructive items are red. Put them only here, behind a ConfirmDialog. */
  tone?: "default" | "danger";
  disabled?: boolean;
  /** Set on choices (workspace, language): the item becomes a radio with a check mark. */
  checked?: boolean;
}

export interface MenuProps {
  /** Accessible name of the trigger, e.g. "More actions for Ana". */
  label: string;
  items: MenuItem[];
}

const ITEM_CLASS =
  "flex min-h-11 w-full items-center px-3 text-left text-operate font-medium whitespace-nowrap hover:bg-blue-wash focus-visible:bg-blue-wash disabled:text-ink-3 disabled:opacity-60";

/** Overflow menu: keyboard accessible (arrows, Home/End, Escape returns focus to the trigger). */
export function Menu({ label, items }: MenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useId();

  // Opening the menu moves focus to its first item, so the keyboard reaches it at once.
  useEffect(() => {
    if (open) listRef.current?.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, [open]);

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const onListKey = (event: KeyboardEvent<HTMLUListElement>) => {
    const buttons = Array.from(
      event.currentTarget.querySelectorAll<HTMLButtonElement>("button:not(:disabled)"),
    );
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (event.key === "Escape") close();
    else if (event.key === "ArrowDown") buttons[(index + 1) % buttons.length]?.focus();
    else if (event.key === "ArrowUp")
      buttons[(index - 1 + buttons.length) % buttons.length]?.focus();
    else if (event.key === "Home") buttons[0]?.focus();
    else if (event.key === "End") buttons[buttons.length - 1]?.focus();
    else return;
    event.preventDefault();
  };

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(event) => {
          if (event.key === "Escape" && open) {
            event.preventDefault();
            close();
          }
        }}
        className="inline-flex size-11 items-center justify-center rounded-control text-ink-2 hover:bg-ice-2 hover:text-ink"
      >
        <EllipsisVertical aria-hidden="true" className="size-[18px]" strokeWidth={1.75} />
      </button>
      {open ? (
        <ul
          ref={listRef}
          id={listId}
          role="menu"
          aria-label={label}
          onKeyDown={onListKey}
          className="absolute right-0 z-20 mt-1 min-w-44 overflow-hidden rounded-control border border-hairline bg-board py-1 shadow-board"
        >
          {items.map((item) => (
            <li key={item.label} role="none">
              <button
                type="button"
                role={item.checked === undefined ? "menuitem" : "menuitemradio"}
                aria-checked={item.checked}
                disabled={item.disabled}
                onClick={() => {
                  setOpen(false);
                  item.onSelect();
                }}
                className={`${ITEM_CLASS} ${item.tone === "danger" ? "text-red" : "text-ink"}`}
              >
                {item.checked === undefined ? null : (
                  <Check
                    aria-hidden="true"
                    className={`mr-2 size-[18px] shrink-0 ${item.checked ? "opacity-100" : "opacity-0"}`}
                    strokeWidth={1.75}
                  />
                )}
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
